import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { AuthLayout } from '@/components/layout/AuthLayout'
import { authService } from '@/services/auth.service'
import { useAuthStore } from '@/stores/authStore'
import { ROUTES } from '@/config/routes'
import { AlertCircle, CheckCircle, Loader2 } from 'lucide-react'

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
  rememberMe: z.boolean().optional()
})

type LoginFormData = z.infer<typeof loginSchema>

export const LoginPage = () => {
  const navigate = useNavigate()
  const { setUser, setToken, setLinkedAccounts } = useAuthStore()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [apiStatus, setApiStatus] = useState<'checking' | 'online' | 'offline'>('checking')

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: true  // Default to true for 30-day token
    }
  })

  // Check API health on component mount
  useEffect(() => {
    const checkApiHealth = async () => {
      setApiStatus('checking')
      const isHealthy = await authService.checkHealth()
      setApiStatus(isHealthy ? 'online' : 'offline')

      if (!isHealthy) {
        setError('Unable to connect to PulseWindow API. Please check your internet connection or try again later.')
      }
    }

    checkApiHealth()

    // Re-check API health every 30 seconds if offline
    const intervalId = setInterval(() => {
      if (apiStatus === 'offline') {
        checkApiHealth()
      }
    }, 30000)

    return () => clearInterval(intervalId)
  }, [])

  const onSubmit = async (data: LoginFormData) => {
    // Prevent login if API is offline
    if (apiStatus === 'offline') {
      setError('Cannot log in while the API is offline. Please wait for the connection to be restored.')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const response = await authService.login({
        email: data.email,
        password: data.password,
        rememberMe: data.rememberMe,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        locale: navigator.language
      })

      setToken(response.token)
      setUser(response.user)
      setLinkedAccounts(response.linkedAccounts)
      navigate(ROUTES.DASHBOARD)
    } catch (err: any) {
      // Check if error is due to network issues
      if (!err.response) {
        setError('Unable to connect to PulseWindow API. Please check your connection.')
        setApiStatus('offline')
      } else {
        setError(err.response?.data?.message || 'Login failed. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AuthLayout>
      <Card>
        <CardHeader>
          <CardTitle>Welcome Back</CardTitle>
          <CardDescription>Sign in to your PulseWindow account</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <CardContent className="space-y-4">
            {/* API Status Indicator */}
            <div className="flex items-center gap-2 text-sm">
              {apiStatus === 'checking' && (
                <>
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  <span className="text-muted-foreground">Checking API connection...</span>
                </>
              )}
              {apiStatus === 'online' && (
                <>
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-green-600">API Connected</span>
                </>
              )}
              {apiStatus === 'offline' && (
                <>
                  <AlertCircle className="h-4 w-4 text-destructive" />
                  <span className="text-destructive">API Offline</span>
                </>
              )}
            </div>

            {error && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive flex items-start gap-2">
                <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <div>{error}</div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                {...register('email')}
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                {...register('password')}
              />
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <input
                id="rememberMe"
                type="checkbox"
                {...register('rememberMe')}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="rememberMe" className="text-sm font-normal">
                Remember me
              </Label>
            </div>
          </CardContent>

          <CardFooter className="flex flex-col space-y-4">
            <Button
              type="submit"
              className="w-full"
              disabled={isLoading || apiStatus === 'offline' || apiStatus === 'checking'}
            >
              {isLoading ? 'Signing in...' : apiStatus === 'offline' ? 'API Offline' : 'Sign In'}
            </Button>

            <Link
              to={ROUTES.FORGOT_PASSWORD}
              className="text-sm text-primary hover:underline"
            >
              Forgot your password?
            </Link>
          </CardFooter>
        </form>
      </Card>
    </AuthLayout>
  )
}
